import { cn } from "@gettactic/helpers";
import { ArrowRight, X } from "lucide-react";
import type { Notification, NotificationType } from "@gettactic/api";
import { Button } from "@cmp-src/primitives/button";

export interface NotificationBannerProps {
	/** The organization notification to show. */
	notification: Notification;
	/** Called with the id when a dismissible notification is closed. */
	onDismiss?: (id: string) => void;
	/** Called when a notification without a `cta` link is opened. */
	onClick?: (notification: Notification) => void;
}

const NOTIFICATION_TYPES_WITH_FALLBACK: NotificationType[] = ["billing_unpaid"];

const severityStyles = {
	default: {
		container: "border-gray-200 bg-gray-50/50 hover:bg-gray-50",
		title: "text-gray-900",
		message: "text-content",
		icon: "text-gray-400",
	}
};

/**
 * An organization notification (maintenance, billing, announcements) on the dashboard. Its
 * arrow button opens the `cta` link or calls `onClick`; the close button calls `onDismiss`.
 */
export const NotificationBanner: React.FC<NotificationBannerProps> = ({
	notification,
	onDismiss,
	onClick,
}) => {
	// for now we keep them gray
	// const styles = severityStyles[notification.severity] || severityStyles.default;
	const styles = severityStyles.default;

	// Check if notification type has a fallback action
	const hasFallbackAction =
		!notification.cta &&
		NOTIFICATION_TYPES_WITH_FALLBACK.includes(notification.type);
	const isClickable = !!notification.cta || hasFallbackAction || !!onClick;
	const isDismissible = notification.is_dismissible && !!onDismiss;

	// const isClickable = true;
	// const isDismissible = true;

	const handleClick = () => {
		if (notification.cta?.url) {
			window.open(notification.cta.url, "_blank", "noopener,noreferrer");
		} else if (onClick) {
			onClick(notification);
		}
	};

	const handleDismiss = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (onDismiss) {
			onDismiss(notification.id);
		}
	};

	return (
		<div
			className={cn(
				"mb-6 mt-2 flex w-full items-stretch rounded-xl border p-4 transition-colors relative",
				{
					"justify-between": isClickable && !isDismissible,
					"justify-start": isClickable && isDismissible,
				},
				styles.container,
				isClickable && "cursor-pointer",
			)}
			// The whole card is a mouse shortcut for its arrow button, which is what the keyboard
			// and assistive tech use: a button role here would nest the card's buttons in it
			onClick={isClickable ? handleClick : undefined}
		>
			<div className="flex flex-col gap-1 pr-4">
				<p className={cn("text-sm font-semibold", styles.title)}>
					{notification.title}
				</p>
				<p className={cn("text-sm", styles.message)}>{notification.message}</p>
			</div>
			<div
				className={cn("flex flex-col items-end", {
					"justify-center": isClickable,
					"justify-start": !isClickable && isDismissible,
					"flex-1 items-center justify-center": isDismissible,
					"ml-4": !isDismissible,
				})}
			>
				{isDismissible && (
					<Button
						type="button"
						onClick={handleDismiss}
						className={cn("absolute -top-2 -right-2")}
						aria-label="Dismiss notification"
						variant="circleIcon"
						size="icon"
					>
						<X className="!size-4" aria-hidden="true" />
					</Button>
				)}
				{isClickable && (
					<Button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							handleClick();
						}}
						className={cn("size-6", styles.icon, {})}
						aria-label={notification.cta?.label || `Open ${notification.title}`}
						variant="circleIcon"
						size="icon"
					>
						<ArrowRight className="!size-6" aria-hidden="true" />
					</Button>
				)}
			</div>
		</div>
	);
};
